import React from "react";
import PropTypes from "prop-types";
import {makeStyles, Grid, Box, Typography, Divider} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import Calendar from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/date-calendar/calendar.svg";
import { useI18N, useDate, useCurrency } from "../../../common/common";
import { useKeycloakFunctions } from "../../../hooks/useKeycloakFunctions";
import moment from "moment";
import {calculateDays} from "../../../common/date";
import {CustomButton, StreamlineIcon} from "@hapinest/theme";
import { getDatesFromToLabel } from "../../../selectors/dates";
import { nbsp } from "../../../common/constant";

const useStyles = makeStyles((theme) => ({
    root: {
        border: "1px solid " + theme.palette.borders.line.light,
        borderRadius: theme.spacing(0.75),
        overflow: "hidden"
    },
    dashedDivider: {
        background: theme.palette.background.default,
        border: "1px dashed " + theme.palette.borders.line.lighter,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    wordBreakDescription: {
        "-webkit-hyphens": "auto",
        "-moz-hyphens": "auto",
        hyphens: "auto"
    },
    whiteSpaceNoWrap: {
        whiteSpace: "nowrap"
    }
}));

const ReservationOptionItem = ({
    title,
    description,
    apartment,
    dateFrom,
    dateTo,
    notAvailableInfoText,
    buttonText,
    onClickButton,
    buttonDisabled,
    notAvailable,
    showDescriptionOnly,
    subTitle,
}) => {
    const classes = useStyles();
    const {i18n, i18nLowerIfRequired} = useI18N();
    const {formatDate} = useDate();
    const {formatCurrency} = useCurrency();
    const {isTrialMember} = useKeycloakFunctions();

    return (
        <Box
            className={classes.root}
            p={2}
        >
            <Grid
                container
                direction="row"
                wrap="nowrap"
                alignItems="center"
                justify="space-between"
                spacing={1}
            >
                {title && !showDescriptionOnly && (
                    <Grid
                        item
                        xs={subTitle ? false : 6}
                        sm="auto"
                    >
                        <Typography
                            variant="h5"
                            noWrap
                        >
                            {!apartment ? <Skeleton width={120}/> : title}
                        </Typography>

                        {subTitle && (
                            <Box
                                mt={1}
                            >
                                {!apartment ? <Skeleton width={120}/> : subTitle}
                            </Box>
                        )}
                    </Grid>
                )}

                {!title && dateFrom && dateTo && !showDescriptionOnly && (
                    <Grid
                        item
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                        >
                            <StreamlineIcon><Calendar/></StreamlineIcon>

                            <Box
                                ml={2}
                            >
                                <Typography
                                    variant="body1"
                                >
                                    {!apartment ? <Skeleton width={220}/> : getDatesFromToLabel(dateFrom, dateTo, "", formatDate, false)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                )}

                {description && (
                    <Grid
                        item
                        xs={showDescriptionOnly ? false : 6}
                        sm={showDescriptionOnly ? false : 5}
                        md="auto"
                    >
                        <Typography
                            variant="body2"
                            align="right"
                            className={classes.wordBreakDescription}
                        >
                            {!apartment ? <Skeleton width={220}/> : description}
                        </Typography>
                    </Grid>
                )}
            </Grid>

            <Divider
                className={classes.dashedDivider}
            />

            <Grid
                container
                direction="row"
                wrap="nowrap"
                alignItems="center"
                justify="space-between"
                spacing={1}
            >
                <Grid
                    item
                    xs={6}
                    sm="auto"
                >
                    {!apartment && (
                        <>
                            <Typography
                                variant="body2"
                                gutterBottom
                            >
                                <Skeleton width={160}/>
                            </Typography>

                            <Typography
                                variant="body1"
                            >
                                <Skeleton width={160}/>
                            </Typography>
                        </>
                    )}

                    {(apartment?.local_costs > -1 && !notAvailable) && (
                        <>
                            {!subTitle ? (
                                <>
                                    <Typography
                                        variant="body2"
                                        gutterBottom
                                    >
                                        {i18n("common.label.price_for") + " " + calculateDays(moment(dateFrom), moment(dateTo)) + " " + (calculateDays(moment(dateFrom), moment(dateTo)) > 1 ? i18nLowerIfRequired("common.label.nights") : i18nLowerIfRequired("common.label.night")) + ":"}
                                    </Typography>

                                    <Typography
                                        variant="subtitle1"
                                    >
                                        {(!apartment.trial_booking_costs && (apartment.total_points >= 0 || apartment.points >= 0)) && (
                                            <>{(apartment.total_points >= 0 ? apartment.total_points : apartment.points)}&nbsp;{i18nLowerIfRequired("common.label.points") + " "}</>
                                        )}
                                        <Box
                                            component="span"
                                            whiteSpace="nowrap"
                                        >
                                            {formatCurrency(apartment.currency, (apartment.trial_booking_costs ? apartment.local_costs + apartment.trial_booking_costs : apartment.local_costs))}
                                        </Box>
                                    </Typography>
                                </>
                            ) : (
                                <Box
                                    display={"flex"}
                                    flexDirection={"column"}
                                >
                                    <Typography
                                        variant="subtitle1"
                                    >
                                        {(!apartment.trial_booking_costs && (apartment.total_points >= 0 || apartment.points >= 0)) && (
                                            <>{(apartment.total_points >= 0 ? apartment.total_points : apartment.points)}{nbsp + i18nLowerIfRequired("common.label.points") + " "}</>
                                        )}
                                        <br />
                                        {(!isTrialMember() ? "+" + nbsp : " ") + formatCurrency(apartment.currency, (apartment.trial_booking_costs ? apartment.local_costs + apartment.trial_booking_costs : apartment.local_costs))}

                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}

                    {notAvailableInfoText && (
                        <Typography
                            variant="subtitle1"
                        >
                            {notAvailableInfoText}
                        </Typography>
                    )}
                </Grid>

                <Grid
                    item
                >
                    {apartment?.local_costs > -1 && (
                        <CustomButton
                            variant={"contained"}
                            disableElevation
                            onClick={onClickButton}
                            loading={apartment?.isLoading}
                            disabled={buttonDisabled}
                            className={classes.whiteSpaceNoWrap}
                        >
                            {buttonText}
                        </CustomButton>
                    )}

                    {apartment?.local_costs === -1 && (
                        <CustomButton
                            variant={"outlined"}
                            disableElevation
                            onClick={onClickButton}
                            loading={apartment?.isLoading}
                            disabled={buttonDisabled || notAvailable}
                            className={classes.whiteSpaceNoWrap}
                        >
                            {buttonText}
                        </CustomButton>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

ReservationOptionItem.propTypes = {
    title: PropTypes.string,
    description: PropTypes.any,
    apartment: PropTypes.object,
    dateFrom: PropTypes.any,
    dateTo: PropTypes.any,
    notAvailableInfoText: PropTypes.string,
    buttonText: PropTypes.string.isRequired,
    onClickButton: PropTypes.func.isRequired,
    buttonDisabled: PropTypes.bool,
    notAvailable: PropTypes.bool,
    showDescriptionOnly: PropTypes.bool,
    subTitle: PropTypes.any
};

export default ReservationOptionItem;
